import React from "react"
import DateTimeField from "components/Common/DateTimeField"
import { COUNTRIES, SPORTS, seasonLabel } from "./plannerQuery"
import { sportLabel } from "./plannerLabels"
import { MODES, REGIONS, SEASONS_BACK, seasonChoices, seasonPeriod } from "./generationRequest"
import { Field, OrganizerSearch } from "./ProposalEventFields"

const CUSTOM = "custom"

// The run's form (ticket 17; the Generate artboard without its progress bar): by organizer or by period, the country, the period — a season
// by start date or a free range — and the seasons back. Generating is a read: nothing is saved before the review's Save.
export default function GenerationForm({ form, errors, settings, today, busy, onChange, onGenerate }) {
  const set = fields => onChange({ ...form, ...fields })
  const period = form.periodKind === "season" ? form.season : CUSTOM
  const toggleSport = sport => set({ sports: form.sports.includes(sport) ? form.sports.filter(item => item !== sport) : SPORTS.filter(item => item === sport
    || form.sports.includes(item)) })
  return <section className="ep-card ep-generate-form" aria-label="Generate proposals">
    <div className="ep-stack">
      <b>Drafts from last season's tournaments</b>
      <span className="ep-meta">Each tournament comes back 52 weeks later, on the same weekday, with its competitions sized by their demand. Areas and
        months without history are shown as gaps, never generated. Nothing is saved before you review the drafts.</span>
    </div>
    <div role="tablist" aria-label="Generate by" className="ep-tabs">
      {MODES.map(([key, label]) => <button type="button" role="tab" key={key} aria-selected={form.mode === key}
        className={"ep-tab" + (form.mode === key ? " is-selected" : "")} onClick={() => set({ mode: key })}>{label}</button>)}
    </div>
    <div className="ep-grid">
      {form.mode === "organizer" && <Field id="generate-organizer" label="Organizer" required wide error={errors.organizer}
        help="Both sports of the organizer.">
        <OrganizerSearch id="generate-organizer" organizer={form.organizer} disabled={busy} onChange={organizer => set({ organizer })} />
      </Field>}
      <Field id="generate-country" label="Country" required>
        <select id="generate-country" className="form-control" value={form.country} disabled={busy}
          onChange={event => set({ country: event.target.value, region: "" })}>
          {COUNTRIES.map(country => <option key={country} value={country}>{country}</option>)}
        </select>
      </Field>
      <Field id="generate-circuit" label="Circuit">
        <input id="generate-circuit" className="form-control" value="Sportya" readOnly /></Field>
      <Field id="generate-period" label="Period" required help={form.periodKind === "season" ? "The tournaments starting in the season's months." : undefined}>
        <select id="generate-period" className="form-control" value={period} disabled={busy} onChange={event => (event.target.value === CUSTOM
          ? set({ periodKind: "range" }) : set({ periodKind: "season", season: event.target.value, ...seasonPeriod(event.target.value) }))}>
          {seasonChoices(today).map(season => <option key={season} value={season}>{seasonLabel(season)}</option>)}
          <option value={CUSTOM}>Custom range (at most 12 months)</option>
        </select>
      </Field>
      <Field id="generate-seasons-back" label="Seasons back" help="Older tournaments come back only when they had no newer edition.">
        <span role="group" aria-label="Seasons back" className="ep-segment">
          {SEASONS_BACK.map(count => <button type="button" key={count} aria-pressed={form.seasonsBack === count} disabled={busy}
            className={"ep-chip" + (form.seasonsBack === count ? " is-on" : "")} onClick={() => set({ seasonsBack: count })}>{count}</button>)}
        </span>
      </Field>
      {form.periodKind === "range" && <>
        <Field id="generate-from" label="From" required error={errors.from}>
          <DateTimeField id="generate-from" mode="date" value={form.from} rangeEnd={form.to || ""} disabled={busy} onChange={event => set({ from: event.target.value })} /></Field>
        <Field id="generate-to" label="To" required error={errors.to}>
          <DateTimeField id="generate-to" mode="date" value={form.to} rangeStart={form.from || ""} disabled={busy} onChange={event => set({ to: event.target.value })} /></Field>
      </>}
      {form.mode === "period" && <>
        <Field id="generate-sports" label="Sport" required error={errors.sports}>
          <span role="group" aria-label="Sport" className="ep-segment">
            {SPORTS.map(sport => <button type="button" key={sport} aria-pressed={form.sports.includes(sport)} disabled={busy}
              className={"ep-chip" + (form.sports.includes(sport) ? " is-on" : "")} onClick={() => toggleSport(sport)}>{sportLabel(sport)}</button>)}
          </span>
        </Field>
        <Field id="generate-category" label="Category (optional)">
          <select id="generate-category" className="form-control" value={form.category} disabled={busy} onChange={event => set({ category: event.target.value })}>
            <option value="">All categories</option>
            {(settings.categories || []).map(category => <option key={category.key} value={category.key}>{category.name}</option>)}
          </select>
        </Field>
        {form.country === "Romania" && <Field id="generate-region" label="Region (optional)">
          <select id="generate-region" className="form-control" value={form.region} disabled={busy} onChange={event => set({ region: event.target.value })}>
            <option value="">All regions</option>
            {REGIONS.map(region => <option key={region} value={region}>{region}</option>)}
          </select>
        </Field>}
      </>}
    </div>
    <div className="ep-generate-actions">
      <span className="ep-meta">It takes a few seconds. Tournaments already proposed or published for an edition are never generated twice.</span>
      <button type="button" className="btn btn-primary" disabled={busy} onClick={onGenerate}>
        <i className="bx bx-bolt-circle" aria-hidden="true" /> {busy ? "Generating…" : "Generate drafts"}</button>
    </div>
  </section>
}
